/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo, useEffect } from 'react';
import { useCurrentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { defaultLocale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { Controller } from './types.tsx';

export function useCurrentUser(): Controller {
	const { data, error, loading, refresh } = useCurrentUserResource();
	const {
		atlassianAccountId: accountId,
		isAdmin,
		isAnonymous,
		isSiteAdmin,
		locale = defaultLocale,
		siteAdminStatus,
		userFullname = '',
	} = useTenantContext();

	useEffect(() => {
		// load on client if not loaded as a resource
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, error, refresh]);

	return useMemo(() => {
		if (isAnonymous) {
			return {
				data: {
					user: {
						accountId,
						isAdmin,
						isAnonymous,
						isSiteAdmin,
						locale,
						siteAdminStatus,
						userFullname: userFullname ?? '',
					},
				},
				loading: false,
				error: null,
			};
		}

		return {
			data: {
				user: {
					accountId: data?.accountId ?? accountId ?? '',
					active: data?.active || false,
					applicationRoles: data?.applicationRoles,
					avatarUrls: data?.avatarUrls ?? {
						'16x16': '',
						'24x24': '',
						'32x32': '',
						'48x48': '',
					},
					displayName: data?.displayName ?? '',
					emailAddress: data?.emailAddress,
					expand: data?.expand,
					groups: data?.groups,
					isAdmin,
					isAnonymous,
					isSiteAdmin,
					key: data?.key,
					locale: data?.locale ?? locale,
					name: data?.name,
					self: data?.self,
					siteAdminStatus,
					userFullname: userFullname ?? '',
					timeZone: data?.timeZone,
				},
			},
			loading,
			error,
		};
	}, [
		accountId,
		data,
		error,
		isAdmin,
		isAnonymous,
		isSiteAdmin,
		loading,
		locale,
		siteAdminStatus,
		userFullname,
	]);
}
